import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailReadOutlined";
import { useTranslation } from "react-i18next";

import { AuthShell } from "../components/AuthShell";
import { LabeledField } from "../components/LabeledField";
import { authApi } from "../api/endpoints";

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await authApi.forgotPassword(email.trim().toLowerCase());
      setSent(true);
    } catch (apiError) {
      setError(apiError.code ?? "errors.unexpected");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthShell
      title={t("auth.heroTitle")}
      subtitle={t("auth.heroSubtitle")}
      trustPoints={[t("auth.trustDocuments"), t("auth.trustReview")]}
    >
      <Box sx={{ width: "100%", maxWidth: 410 }}>
        {sent ? (
          <Stack spacing={2.5} sx={{ textAlign: "center" }}>
            <Box
              sx={{
                width: 58,
                height: 58,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "success.light",
                color: "success.main",
                mx: "auto",
              }}
            >
              <MarkEmailReadIcon fontSize="large" />
            </Box>
            <Typography variant="h2">{t("auth.forgotTitle")}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t("auth.resetEmailSent")}
            </Typography>
            <Button
              fullWidth
              size="large"
              variant="contained"
              startIcon={<ChevronLeftIcon />}
              onClick={() => navigate("/")}
            >
              {t("auth.backToSignIn")}
            </Button>
          </Stack>
        ) : (
          <Box component="form" onSubmit={submit} noValidate>
            <Typography variant="h2">{t("auth.forgotTitle")}</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 1, mb: 3 }}
            >
              {t("auth.forgotSubtitle")}
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2.5 }}>
                {t(error, { defaultValue: t("errors.unexpected") })}
              </Alert>
            )}

            <LabeledField
              label={t("auth.email")}
              type="email"
              required
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />

            <Button
              type="submit"
              fullWidth
              size="large"
              variant="contained"
              disabled={submitting}
              sx={{ mt: 3 }}
            >
              {submitting ? (
                <>
                  <CircularProgress
                    size={18}
                    color="inherit"
                    sx={{ mr: 1.5 }}
                  />
                  {t("common.sending")}
                </>
              ) : (
                t("auth.sendLink")
              )}
            </Button>

            <Button
              fullWidth
              size="small"
              startIcon={<ChevronLeftIcon />}
              onClick={() => navigate("/")}
              sx={{ mt: 2 }}
            >
              {t("auth.backToSignIn")}
            </Button>
          </Box>
        )}
      </Box>
    </AuthShell>
  );
}
